import { axiosInstance } from "@/lib/axios-instance";
import {
  IInboundEmail,
  IInboundEmailExtractedInfo,
  InboundEmailStatus,
  InboxFilter,
} from "@/types/data-models";

interface InboxListResponse {
  status: string;
  emails: IInboundEmail[];
  counts: {
    needs_review: number;
    linked: number;
    ignored: number;
    /** Emails still in the review pile that the automatic run could not read. */
    needs_info?: number;
  };
}

export interface InboxDetailResponse {
  status: string;
  email: IInboundEmail;
  thread: IInboundEmail[];
  /** Importable deck links found in each message's body, keyed by email id. */
  deck_links_by_email?: Record<string, string[]>;
  /** Whether to offer creating a deal from this email. Derived server-side. */
  auto_create_suggested?: boolean;
}

interface InboxUpdateResponse {
  status: string;
  email: IInboundEmail;
}

export const inboxApi = {
  listEmails: async (
    workspaceId: string,
    status?: InboxFilter,
    search?: string
  ) => {
    const response = await axiosInstance.get<InboxListResponse>(
      "/inbox-emails",
      {
        params: {
          workspace_id: workspaceId,
          ...(status && { status }),
          ...(search && { search }),
        },
      }
    );
    return response.data;
  },

  getEmail: async (emailId: string) => {
    const response = await axiosInstance.get<InboxDetailResponse>(
      `/inbox-emails/${emailId}`
    );
    return response.data;
  },

  linkToDeal: async (
    emailId: string,
    dealId: string,
    companyId?: string,
    fileTypes?: Record<string, string>,
    workspaceId?: string
  ) => {
    const response = await axiosInstance.put<InboxUpdateResponse>(
      `/inbox-emails/${emailId}/link`,
      {
        deal_id: dealId,
        company_id: companyId,
        ...(workspaceId && { workspace_id: workspaceId }),
        ...(fileTypes && { file_types: fileTypes }),
      }
    );
    return response.data;
  },

  attachFilesToCompany: async (
    emailId: string,
    companyId: string,
    fileTypes: Record<string, string>
  ) => {
    const response = await axiosInstance.put<{ status: string }>(
      `/inbox-emails/${emailId}/attach-files`,
      {
        company_id: companyId,
        file_types: fileTypes,
      }
    );
    return response.data;
  },

  updateStatus: async (emailId: string, status: InboundEmailStatus) => {
    const response = await axiosInstance.put<InboxUpdateResponse>(
      `/inbox-emails/${emailId}/status`,
      { status }
    );
    return response.data;
  },

  /**
   * Correct the AI's extraction. Writes to an overrides layer server-side, so
   * a re-run of the extraction worker can't clobber it. `null` resets a field
   * back to whatever the AI said.
   */
  updateExtractedInfo: async (
    emailId: string,
    overrides: Record<string, string | null>
  ) => {
    const response = await axiosInstance.patch<{
      status: string;
      extracted_info?: IInboundEmailExtractedInfo;
    }>(`/inbox-emails/${emailId}/extracted-info`, { overrides });
    return response.data;
  },

  /** Edit the message. Editing the body switches it to plain text. */
  updateContent: async (
    emailId: string,
    content: { subject?: string; text?: string }
  ) => {
    const response = await axiosInstance.patch<InboxUpdateResponse>(
      `/inbox-emails/${emailId}/content`,
      content
    );
    return response.data;
  },

  /** "Not a deal" — stop offering to create one from this email. */
  dismissAutoCreate: async (emailId: string) => {
    const response = await axiosInstance.patch<{ status: string }>(
      `/inbox-emails/${emailId}/dismiss-auto-create`
    );
    return response.data;
  },

  deleteEmail: async (emailId: string) => {
    const response = await axiosInstance.delete<{ status: string }>(
      `/inbox-emails/${emailId}`
    );
    return response.data;
  },
};
